import { apiClient } from './client';
import type { Firm, CreateFirmRequest, UpdateFirmRequest, FirmHistory, FirmFinancial, CreateFinancialRequest, FinancialSummary, FirmRole, CreateFirmRoleRequest, UpdateFirmRoleRequest, RolePermissions } from '../types';

export const firmsApi = {
  getAll: async (): Promise<Firm[]> => {
    const response = await apiClient.get<Firm[]>('/api/firms');
    return response.data;
  },

  getById: async (id: number): Promise<Firm> => {
    const response = await apiClient.get<Firm>(`/api/firms/${id}`);
    return response.data;
  },

  create: async (data: CreateFirmRequest): Promise<Firm> => {
    const response = await apiClient.post<Firm>('/api/firms', data);
    return response.data;
  },

  update: async (id: number, data: UpdateFirmRequest): Promise<Firm> => {
    const response = await apiClient.put<Firm>(`/api/firms/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/firms/${id}`);
  },

  // License management
  updateLicense: async (id: number, data: { licenseExpiresAt?: string; licenseStatus: string }): Promise<Firm> => {
    const response = await apiClient.put<Firm>(`/api/firms/${id}/license`, data);
    return response.data;
  },

  activateFirm: async (id: number): Promise<Firm> => {
    const response = await apiClient.post<Firm>(`/api/firms/${id}/activate`);
    return response.data;
  },

  deactivateFirm: async (id: number, reason?: string): Promise<Firm> => {
    const response = await apiClient.post<Firm>(`/api/firms/${id}/deactivate`, { reason });
    return response.data;
  },

  getExpiringLicenses: async (days: number = 30): Promise<Firm[]> => {
    const response = await apiClient.get<Firm[]>(`/api/firms/expiring?days=${days}`);
    return response.data;
  },

  // History
  getHistory: async (id: number, type?: string): Promise<FirmHistory[]> => {
    const url = type ? `/api/firms/${id}/history?type=${type}` : `/api/firms/${id}/history`;
    const response = await apiClient.get<FirmHistory[]>(url);
    return response.data;
  },

  // Financials
  getFinancials: async (id: number): Promise<FirmFinancial[]> => {
    const response = await apiClient.get<FirmFinancial[]>(`/api/firms/${id}/financials`);
    return response.data;
  },

  getFinancialSummary: async (id: number): Promise<FinancialSummary> => {
    const response = await apiClient.get<FinancialSummary>(`/api/firms/${id}/financials/summary`);
    return response.data;
  },

  addFinancial: async (id: number, data: CreateFinancialRequest): Promise<FirmFinancial> => {
    const response = await apiClient.post<FirmFinancial>(`/api/firms/${id}/financials`, data);
    return response.data;
  },

  updateFinancial: async (firmId: number, financialId: number, data: CreateFinancialRequest): Promise<FirmFinancial> => {
    const response = await apiClient.put<FirmFinancial>(`/api/firms/${firmId}/financials/${financialId}`, data);
    return response.data;
  },

  deleteFinancial: async (firmId: number, financialId: number): Promise<void> => {
    await apiClient.delete(`/api/firms/${firmId}/financials/${financialId}`);
  },

  // Roles
  getRoles: async (firmId: number): Promise<FirmRole[]> => {
    const response = await apiClient.get<FirmRole[]>(`/api/firms/${firmId}/roles`);
    return response.data;
  },

  createRole: async (firmId: number, data: CreateFirmRoleRequest): Promise<FirmRole> => {
    const response = await apiClient.post<FirmRole>(`/api/firms/${firmId}/roles`, data);
    return response.data;
  },

  updateRole: async (firmId: number, roleId: number, data: UpdateFirmRoleRequest): Promise<FirmRole> => {
    const response = await apiClient.put<FirmRole>(`/api/firms/${firmId}/roles/${roleId}`, data);
    return response.data;
  },

  deleteRole: async (firmId: number, roleId: number): Promise<void> => {
    await apiClient.delete(`/api/firms/${firmId}/roles/${roleId}`);
  },

  getRolePermissions: async (firmId: number, roleId: number): Promise<RolePermissions> => {
    const response = await apiClient.get<RolePermissions>(`/api/firms/${firmId}/roles/${roleId}/permissions`);
    return response.data;
  },

  updateRolePermissions: async (firmId: number, roleId: number, data: RolePermissions): Promise<RolePermissions> => {
    const response = await apiClient.put<RolePermissions>(`/api/firms/${firmId}/roles/${roleId}/permissions`, data);
    return response.data;
  },
};
